/* Aura mini bundle: native variants with Shopify automatic quantity-tier savings. */
(() => {
  'use strict';
  const count = selection => Object.values(selection).reduce((sum, qty) => sum + qty, 0);
  const ceiling = (variant, maximum, inCart = 0) => Math.max(0, Math.min(maximum,
    variant.inventory == null ? maximum : variant.inventory - inCart,
    variant.max == null ? maximum : variant.max - inCart));
  const clean = (saved, variants, maximum) => {
    const result = {};
    if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return result;
    for (const [id, raw] of Object.entries(saved)) {
      const v = variants.get(id);
      if (!v?.available || !Number.isSafeInteger(raw) || raw <= 0) continue;
      let qty = Math.min(raw, ceiling(v, maximum), maximum - count(result));
      qty = v.min + Math.floor((qty - v.min) / v.step) * v.step;
      if (qty >= v.min) result[id] = qty;
    }
    return result;
  };
  const total = (selection, variants) => Object.entries(selection).reduce((sum, [id, qty]) => sum + variants.get(id).price * qty, 0);
  // Exposed only to Node's test runner; no browser global API.
  if (typeof module !== 'undefined' && module.exports) module.exports = { count, ceiling, clean, total };
  if (typeof window === 'undefined' || customElements.get('aura-mini-bundle')) return;

  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try { return await fetch(url, { ...options, signal: controller.signal }); }
    finally { clearTimeout(timer); }
  };
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  const button = (text, label, action) => {
    const node = el('button', '', text); node.type = 'button';
    node.setAttribute('aria-label', label); node.addEventListener('click', action); return node;
  };
  class AuraMiniBundle extends HTMLElement {
    async connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.minimum = Number(this.dataset.min) || 3;
      this.maximum = Math.max(this.minimum, Number(this.dataset.max) || 12);
      this.key = `aura-mini-bundle:v1:${this.dataset.section}:${this.dataset.currency}:${this.dataset.country || ''}:${this.dataset.locale || ''}`;
      this.pending = false; this.locked = false; this.filter = 'all'; this.search = ''; this.selection = {};
      this.preview = false;
      let catalog = [];
      try { catalog = JSON.parse(this.dataset.catalog || '[]'); } catch (_) { /* Invalid source leaves an empty catalog. */ }
      if (!catalog.length && String(window.Shopify?.theme?.id) === '191122211125' && window.Shopify?.theme?.role !== 'main') {
        try {
          const response = await request(this.dataset.previewCatalog);
          if (!response.ok) throw new Error('Preview unavailable');
          const references = await response.json();
          catalog = references.map((v, i) => ({ id: String(900000 + i), productId: `preview-${i}`, title: v.title, variantTitle: 'Reference mini', scentAccent: v.accent, concentration: v.concentration, url: v.previewUrl, image: v.image, price: 2995, available: true, group: v.reference.startsWith('MEN-') ? 'men' : 'women', inventory: null, min: 1, step: 1, max: null }));
          this.preview = true; this.dataset.currency = 'USD'; this.key += ':preview';
          this.querySelector('[data-preview-notice]').hidden = false;
        } catch (_) { /* Public catalog remains empty when preview cannot load. */ }
      }
      this.minimum = this.maximum = 5;
      this.variants = new Map(); this.products = new Map();
      try {
        for (const raw of catalog) {
          const v = { ...raw, id: String(raw.id), min: Math.max(1, Number(raw.min) || 1), step: Math.max(1, Number(raw.step) || 1) };
          if (!/^\d+$/.test(v.id) || !Number.isSafeInteger(v.price) || v.price < 0) continue;
          if (this.variants.has(v.id)) { this.variants.get(v.id).groups.add(v.group); continue; }
          v.groups = new Set([v.group]); if (v.unisex) v.groups.add('unisex'); this.variants.set(v.id, v);
          if (!this.products.has(v.productId)) this.products.set(v.productId, []);
          this.products.get(v.productId).push(v);
        }
        this.money = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency });
      } catch (_) {
        this.querySelector('[data-bundle-app]').hidden = false;
        this.message('The mini selection could not load. Please refresh the page.'); return;
      }
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.key) || 'null');
        if (saved && Date.now() - saved.at < 86400000) {
          if ([5, 7, 10].includes(saved.target)) this.minimum = this.maximum = saved.target;
          this.selection = clean(saved.selection, this.variants, Math.min(10, Math.max(this.maximum, count(saved.selection || {}))));
          this.locked = Boolean(saved.unconfirmed);
        }
      } catch (_) { /* Private browsing can disable storage. Selection still works. */ }
      const seed = new URLSearchParams(location.search).get('scent');
      if (seed && this.variants.has(seed) && !this.locked && !this.selection[seed] && count(this.selection) < this.maximum) {
        this.selection[seed] = this.variants.get(seed).min; this.save();
      }
      this.querySelector('[data-bundle-app]').hidden = false;
      this.querySelectorAll('[data-filter]').forEach(node => node.addEventListener('click', () => {
        this.filter = node.dataset.filter;
        this.querySelectorAll('[data-filter]').forEach(b => b.setAttribute('aria-pressed', String(b === node)));
        this.renderGrid();
      }));
      this.querySelectorAll('[data-target]').forEach(node => node.addEventListener('click', () => {
        const target = Number(node.dataset.target);
        if (this.pending || this.locked) return;
        const needsRemoval = count(this.selection) > target;
        this.minimum = this.maximum = target;
        this.querySelectorAll('[data-target]').forEach(b => b.setAttribute('aria-pressed', String(b === node)));
        this.save(); this.render(); if (needsRemoval) { this.message(`Remove ${count(this.selection) - target} to complete your ${target}-mini set.`); this.openSummary(); }
      }));
      this.querySelector('[data-search]').addEventListener('input', event => { this.search = event.target.value.trim().toLowerCase(); this.renderGrid(); });
      this.querySelectorAll('[data-submit]').forEach(node => node.addEventListener('click', () => this.submit()));
      this.querySelector('[data-reset]').addEventListener('click', () => {
        if (this.pending) return;
        this.locked = false; this.selection = {}; this.save(); this.message('Selection cleared. You can choose your minis again.'); this.render();
      });
      this.querySelector('[data-open-summary]').addEventListener('click', event => {
        event.preventDefault(); this.openSummary();
      });
      window.addEventListener('popstate', () => { if (history.state?.auraBundleReview === this.dataset.section) this.openSummary(false); else if (this.hasAttribute('data-summary-open')) this.closeSummary(true); });
      this.querySelector('[data-close-summary]').addEventListener('click', () => this.closeSummary());
      this.querySelector('.aura-mini-bundle__summary').addEventListener('keydown', event => {
        if (!this.hasAttribute('data-summary-open')) return;
        if (event.key === 'Escape') this.closeSummary();
        if (event.key === 'Tab') { const controls = [...event.currentTarget.querySelectorAll('button:not(:disabled),a[href]')].filter(n=>n.getClientRects().length); const first=controls[0],last=controls.at(-1); if (event.shiftKey && (document.activeElement===first || document.activeElement===event.currentTarget)) { event.preventDefault();last?.focus(); } else if (!event.shiftKey && document.activeElement===last) { event.preventDefault();first?.focus(); } }
      });
      if (this.locked) this.message('A previous cart request needs checking. Review your bag before starting another selection.');
      this.renderGrid(); this.render();
    }
    openSummary(push = true) {
      const summary = this.querySelector('.aura-mini-bundle__summary'); summary.tabIndex = -1;
      if (matchMedia('(max-width:767px)').matches) {
        if (this.hasAttribute('data-summary-open')) return;
        if (push) history.pushState({...history.state, auraBundleReview:this.dataset.section}, '', location.href);
        this.setAttribute('data-summary-open',''); this.previousOverflow=document.body.style.overflow; document.body.style.overflow='hidden'; summary.setAttribute('role','dialog'); summary.setAttribute('aria-modal','true');
      } else summary.scrollIntoView({behavior:'auto',block:'start'});
      summary.focus({preventScroll:true});
    }
    closeSummary(fromHistory = false) {
      if (!fromHistory && history.state?.auraBundleReview === this.dataset.section) { history.back(); return; }
      this.removeAttribute('data-summary-open'); document.body.style.overflow = this.previousOverflow || '';
      const summary = this.querySelector('.aura-mini-bundle__summary'); summary.removeAttribute('role'); summary.removeAttribute('aria-modal');
      this.querySelector('[data-open-summary]').focus({preventScroll:true});
    }
    save(unconfirmed = this.locked) {
      try { sessionStorage.setItem(this.key, JSON.stringify({ selection: this.selection, target: this.minimum, at: Date.now(), unconfirmed })); } catch (_) {}
    }
    message(text) { const node = this.querySelector('[data-status]'); node.textContent = text; node.classList.toggle('aura-status-alert', /could|interrupt|previous|already|exceed|failed|refresh|review your bag/i.test(text)); }
    price(cents) { return this.money.format(cents / 100); }
    label(v) { return v.title + (v.variantTitle && v.variantTitle !== 'Default Title' ? ` — ${v.variantTitle}` : ''); }
    image(v) {
      if (!v.image) return el('span', '', 'Image coming soon');
      const image = el('img'); image.src = v.image; image.alt = this.label(v); image.width = 700; image.height = 700; image.loading = 'lazy'; return image;
    }
    allowed(v) {
      const current = this.selection[v.id] || 0;
      const next = current ? current + v.step : v.min;
      return v.available && !this.pending && !this.locked && next <= ceiling(v, this.maximum) && count(this.selection) - current + next <= this.maximum;
    }
    change(id, direction) {
      if (this.pending || this.locked) return;
      const v = this.variants.get(id); const current = this.selection[id] || 0;
      if (direction > 0 && !this.allowed(v)) return;
      const next = direction > 0 ? (current ? current + v.step : v.min) : current - v.step;
      if (next < v.min) delete this.selection[id]; else this.selection[id] = next;
      this.save(); this.render(); this.message(`${count(this.selection)} bottles selected.`);
    }
    renderGrid() {
      const grid = this.querySelector('[data-grid]'); grid.replaceChildren();
      for (const variants of this.products.values()) {
        const visible = variants.filter(v => this.filter === 'all' || v.groups.has(this.filter) || v.groups.has('all'));
        if (!visible.length || (this.search && !visible.some(v => `${v.title} ${v.scentAccent || ''}`.toLowerCase().includes(this.search)))) continue;
        const card = el('article', 'aura-mini-bundle__card');
        const media = el('button', 'aura-mini-bundle__media'); media.type = 'button'; media.dataset.mediaAdd = '';
        const heading = el('h3'); const link = el('a'); heading.append(link);
        const accent = el('p', 'aura-mini-bundle__accent');
        const format = el('p', 'aura-mini-bundle__format');
        const curation = el('p', 'aura-mini-bundle__curation');
        const price = el('span'); const select = el('select');
        select.setAttribute('aria-label', `Choose ${visible[0].title} size or option`);
        visible.forEach(v => { const option = el('option', '', v.variantTitle === 'Default Title' ? 'Standard' : v.variantTitle); option.value = v.id; option.disabled = !v.available; select.append(option); });
        const initial = visible.find(v => v.available) || visible[0]; select.value = initial.id; media.addEventListener('click', () => this.change(select.value, 1));
        if (visible.length === 1) select.hidden = true;
        const add = button('Add +', `Add ${this.label(initial)}`, () => this.change(select.value, 1)); add.dataset.add = '';
        const update = () => {
          const v = this.variants.get(select.value); media.replaceChildren(this.image(v)); media.dataset.variant = v.id; media.setAttribute('aria-label', `Add ${this.label(v)}`); link.href = v.url; link.textContent = v.title;
          accent.textContent = v.scentAccent || ''; accent.hidden = !v.scentAccent;
          const size = visible.length === 1 && v.variantTitle !== 'Default Title' ? v.variantTitle : '';
          format.textContent = [v.concentration, size].filter(Boolean).join(' · '); format.hidden = !format.textContent;
          curation.textContent = v.curationLine || 'Curated by Aura Supply';
          price.hidden = true; price.textContent = ''; add.dataset.variant = v.id; add.setAttribute('aria-label', `Add ${this.label(v)}`);
          this.updateAdd(add);
        };
        const stepper = el('div', 'aura-mini-bundle__card-stepper'); stepper.dataset.stepper = initial.id;
        const minus = button('−', `Remove one ${this.label(initial)}`, () => this.change(select.value, -1));
        const qty = el('span'); qty.dataset.cardQuantity = '';
        const plus = button('+', `Add one ${this.label(initial)}`, () => this.change(select.value, 1)); plus.dataset.cardPlus = '';
        stepper.append(minus, qty, plus);
        select.addEventListener('change', () => { stepper.dataset.stepper = select.value; update(); this.render(); }); card.append(media, heading, accent, format, curation, price, select, add, stepper); update(); grid.append(card);
      }
      this.querySelector('[data-empty]').hidden = grid.childElementCount > 0;
      this.render();
      const emptyCatalog = this.variants.size === 0;
      this.querySelector('.aura-mini-bundle__filters').hidden = emptyCatalog;
      this.querySelector('.aura-mini-bundle__summary').hidden = emptyCatalog;
      this.querySelector('.aura-mini-bundle__mobile-tray').hidden = emptyCatalog;
    }
    updateAdd(node) {
      const v = this.variants.get(node.dataset.variant); node.disabled = !this.allowed(v);
      node.textContent = !v.available ? 'Sold out' : 'Add +'; node.hidden = Boolean(this.selection[v.id]); node.closest('article').classList.toggle('has-selection', Boolean(this.selection[v.id]));
    }
    render() {
      this.querySelectorAll('[data-target]').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.target) === this.minimum)));
      const quantity = count(this.selection); const offer = {5:5995,7:7995,10:10995}[this.minimum]; const cost = this.dataset.currency === 'USD' ? this.price(offer) : 'Calculated in bag';
      this.querySelectorAll('[data-target]').forEach(b => { let label = b.querySelector('[data-offer-price]'); if (!label) { label = el('small'); label.dataset.offerPrice=''; b.append(label); } label.textContent = this.dataset.currency === 'USD' ? this.price({5:5995,7:7995,10:10995}[b.dataset.target]) : 'Automatic savings'; });
      this.querySelector('[data-meter]').max = this.minimum; this.querySelector('[data-meter]').value = quantity;
      this.querySelector('[data-count]').textContent = `${quantity} of ${this.minimum}`;
      this.querySelector('[data-mobile-count]').textContent = `${quantity} / ${this.minimum}`;
      this.querySelector('[data-total]').textContent = cost; this.querySelector('[data-mobile-total]').textContent = cost;
      this.querySelector('[data-progress]').textContent = quantity > this.minimum ? `Remove ${quantity - this.minimum} to finish your ${this.minimum}-mini set. Your other choices stay saved.` : quantity < this.minimum ? `Add ${this.minimum - quantity} more ${this.minimum - quantity === 1 ? 'mini' : 'minis'}. Mix scents or repeat a favourite.` : `Your ${quantity}-mini set is ready.`;
      const slots = this.querySelector('[data-slots]'); slots.replaceChildren();
      const selected = Object.entries(this.selection).flatMap(([id, qty]) => Array(qty).fill(this.variants.get(id)));
      for (let n = 0; n < selected.length; n++) {
        const slot = el('li', 'aura-mini-bundle__slot');
        if (selected[n]) slot.append(this.image(selected[n]));
        slot.title = this.label(selected[n]); slots.append(slot);
      }
      const lines = this.querySelector('[data-lines]');
      // Keep keyboard focus on the equivalent control after quantity changes.
      const focused = document.activeElement; const focusKey = lines.contains(focused) ? focused?.dataset.focusKey : null;
      lines.replaceChildren();
      for (const [id, qty] of Object.entries(this.selection)) {
        const v = this.variants.get(id); const row = el('div', 'aura-mini-bundle__line');
        row.append(this.image(v), el('p', '', this.label(v)));
        const controls = el('div', 'aura-mini-bundle__quantity');
        const minus = button('−', `Decrease ${this.label(v)}`, () => this.change(id, -1)); minus.dataset.focusKey = `minus-${id}`;
        const plus = button('+', `Increase ${this.label(v)}`, () => this.change(id, 1)); plus.dataset.focusKey = `plus-${id}`; plus.disabled = !this.allowed(v);
        const remove = button('Remove', `Remove ${this.label(v)}`, () => { delete this.selection[id]; this.save(); this.render(); this.message(`${this.label(v)} removed.`); }); remove.className = 'aura-mini-bundle__remove'; remove.dataset.focusKey = `remove-${id}`;
        minus.disabled = remove.disabled = this.pending || this.locked;
        controls.append(minus, el('span', '', String(qty)), plus, remove); row.append(controls); lines.append(row);
      }
      if (focusKey) {
        const next = [...lines.querySelectorAll('[data-focus-key]')].find(node => node.dataset.focusKey === focusKey && !node.disabled);
        if (next) next.focus({ preventScroll: true });
        else { const summary = this.querySelector('.aura-mini-bundle__summary'); summary.tabIndex = -1; summary.focus({ preventScroll: true }); }
      }
      this.querySelectorAll('[data-add]').forEach(node => this.updateAdd(node));
      this.querySelectorAll('[data-media-add]').forEach(node => { node.disabled = !this.allowed(this.variants.get(node.dataset.variant)); });
      this.querySelectorAll('[data-stepper]').forEach(node => {
        const id = node.dataset.stepper; const qty = this.selection[id] || 0; node.hidden = !qty;
        node.querySelector('[data-card-quantity]').textContent = qty;
        node.querySelectorAll('button').forEach(b => b.disabled = this.pending || this.locked);
        node.querySelector('[data-card-plus]').disabled = !this.allowed(this.variants.get(id));
      });
      this.querySelectorAll('[data-submit]').forEach(node => { node.disabled = this.preview || this.pending || this.locked || quantity !== this.minimum; node.textContent = this.preview ? 'Design preview · checkout unavailable' : this.pending ? 'Adding…' : quantity > this.minimum ? `Remove ${quantity - this.minimum} ${quantity - this.minimum === 1 ? "mini" : "minis"}` : quantity < this.minimum ? `Choose ${this.minimum - quantity} more` : `Add ${quantity} minis to bag${this.dataset.currency === 'USD' ? ' — ' + cost : ''}`; });
      this.querySelectorAll('select').forEach(node => { node.disabled = this.pending || this.locked; });
      this.querySelector('[data-review]').hidden = !this.locked;
      this.setAttribute('aria-busy', String(this.pending));
    }
    async submit() {
      if (this.preview || this.pending || this.locked || window.__auraMiniCartPending || count(this.selection) !== this.minimum) return;
      this.pending = true; window.__auraMiniCartPending = true; this.message('Checking your selection…'); this.render();
      const root = (window.Shopify?.routes?.root || this.dataset.root || '/').replace(/\/?$/, '/');
      let attempted = false;
      try {
        const cartResponse = await request(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('We could not check your bag. Please try again.');
        const cart = await cartResponse.json();
        if (cart.currency && cart.currency !== this.dataset.currency) throw new Error('Your currency has changed. Refresh this page before adding.');
        if (cart.items.some(item => this.variants.has(String(item.variant_id)))) throw new Error('Your bag already has minis. Review those first: one bundle offer applies per order. Your new selection is saved.');
        for (const [id, qty] of Object.entries(this.selection)) {
          const v = this.variants.get(id); const existing = cart.items.filter(item => String(item.variant_id) === id).reduce((sum, item) => sum + item.quantity, 0);
          if (qty > ceiling(v, this.maximum, existing)) throw new Error(`${this.label(v)} exceeds the available quantity, including bottles already in your bag. Reduce the quantity or review your bag.`);
        }
        // A marker survives reload during an uncertain POST. Never replay it automatically.
        attempted = true; this.save(true);
        const response = await request(`${root}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: Object.entries(this.selection).map(([id, quantity]) => ({ id, quantity })) }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Shopify could not add the full selection.');
        this.selection = {}; this.save(false); this.message('Your minis are in your bag.');
        window.location.assign(this.dataset.cartUrl || `${root}cart`);
        // Keep controls blocked until navigation so a second tap cannot duplicate the request.
        return;
      } catch (error) {
        this.locked = attempted;
        this.querySelector('[data-review]').hidden = false;
        this.message(`${error.message || 'The connection was interrupted.'}${attempted ? ' Review your bag before trying again; some items may have been added.' : ''}`);
        this.save(); this.pending = false; window.__auraMiniCartPending = false; this.render(); this.querySelector('[data-review]').hidden = false;
      }
    }
  }
  customElements.define('aura-mini-bundle', AuraMiniBundle);
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    window.__auraMiniCartPending = false;
    document.querySelectorAll('aura-mini-bundle').forEach(component => {
      if (!component.ready || !component.money) return;
      component.pending = false; component.render();
    });
  });
})();
